import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../integrations/supabase/client';

const { FiAward, FiTarget, FiTrendingUp } = FiIcons;

const About = ({ showLeadership = true }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  // State for dynamic content
  const [content, setContent] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      const { data } = await supabase
        .from('page_content')
        .select('*')
        .eq('page', 'about');
      
      if (data) {
        const contentMap = {};
        data.forEach(item => {
          contentMap[`${item.section}.${item.key}`] = {
            val: isRTL ? (item.content_ar || item.content_en) : item.content_en,
            img: item.content_en // Image URL usually in English field
          };
        });
        setContent(contentMap);
      }
    };
    
    fetchContent();
  }, [isRTL]);

  // Helper: Get text from DB or fallback to i18n
  const getText = (key, fallbackKey) => content?.[key]?.val || t(fallbackKey);
  const getImage = (key, fallbackUrl) => content?.[key]?.img || fallbackUrl;

  const values = [
    {
      icon: FiAward,
      title: t('about.quality'),
      description: t('about.qualityText')
    },
    {
      icon: FiTarget,
      title: t('about.authenticity'),
      description: t('about.authenticityText')
    },
    {
      icon: FiTrendingUp,
      title: t('about.innovation'),
      description: t('about.innovationText')
    }
  ];

  return (
    <section id="about" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-green-800 mb-6">
            {getText('header.title', 'about.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-4">
             {getText('header.subtitle', 'about.subtitle')}
          </p>
          <div className={`flex items-center justify-center space-x-4 ${isRTL ? 'space-x-reverse' : ''}`}>
            <motion.img 
              src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759920909971-NEXT%20SUPPLY%20PNG.png" 
              alt="Next Supply Logo" 
              className="w-16 h-16 object-contain"
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ type: "spring", stiffness: 300 }}
            />
            <span className="text-green-700 font-medium text-lg">
              {isRTL ? 'بفخر جزء من عائلة نكست سابلاي' : 'Proudly part of the Next Supply family'}
            </span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <div className="relative">
              <motion.img 
                src={getImage('main_image.image', "https://images.unsplash.com/photo-1514432324607-a09d9b4aefdd?ixlib=rb-4.0.3&auto=format&fit=crop&w=1587&q=80")}
                alt="Turkish Coffee" 
                className="rounded-2xl shadow-2xl w-full h-96 object-cover"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              />
              <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-green-700 to-green-800 text-white p-6 rounded-xl shadow-xl">
                <p className="text-3xl font-bold">2017</p>
                <p className="text-sm">{isRTL ? 'منذ' : 'Since'}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <div>
              <h3 className="text-3xl font-bold text-green-800 mb-4">
                 {getText('header.title', 'about.title')}
              </h3>
              <motion.p 
                className="text-gray-600 leading-relaxed mb-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                {getText('intro.description', 'about.description')}
              </motion.p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <motion.div
                className="bg-green-50 p-6 rounded-xl"
                whileHover={{ scale: 1.03, y: -5 }}
              >
                <h4 className="text-xl font-bold text-green-800 mb-2">
                  {getText('mission.title', 'about.mission')}
                </h4>
                <p className="text-gray-600 text-sm">
                   {getText('mission.text', 'about.missionText')}
                </p>
              </motion.div>
              <motion.div
                className="bg-amber-50 p-6 rounded-xl"
                whileHover={{ scale: 1.03, y: -5 }}
              >
                <h4 className="text-xl font-bold text-amber-800 mb-2">
                   {getText('vision.title', 'about.vision')}
                </h4>
                <p className="text-gray-600 text-sm">
                   {getText('vision.text', 'about.visionText')}
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* ... (Keep existing Values and Leadership sections static for now to avoid complexity overload in one go) ... */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <h3 className="text-3xl font-bold text-green-800 text-center mb-12">
            {t('about.values')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gradient-to-br from-green-50 to-amber-50 p-8 rounded-2xl shadow-lg text-center"
              >
                <div className="bg-gradient-to-br from-green-600 to-green-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={value.icon} className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-xl font-bold text-green-800 mb-3">{value.title}</h4>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {showLeadership && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-20"
          >
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-green-800 mb-4">{t('about.team')}</h3>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('about.teamDescription')}</p>
            </div>
            {/* Keeping leadership static for this step */}
            <div className="max-w-4xl mx-auto space-y-12">
              {/* Mohsen Tohami */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="bg-gradient-to-br from-green-50 to-amber-50 rounded-2xl p-8 shadow-lg flex flex-col md:flex-row items-center md:items-start gap-8"
              >
                <img src="/MohsenTohamiFounderManagingDirector.jpg" alt="Mohsen Tohami" className="w-40 h-40 rounded-full object-cover shadow-md border-4 border-white" />
                <div className="text-center md:text-left">
                  <h4 className="text-2xl font-bold text-green-800 mb-2">{isRTL ? t('about.mohsenTohami') : 'Mohsen Tohami'}</h4>
                  <p className="text-green-600 font-semibold mb-4">{t('about.founderManagingDirector')}</p>
                  <p className="text-gray-600 text-sm leading-relaxed">{t('about.mohsenBio')}</p>
                </div>
              </motion.div>

              {/* Ali Taha */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-gradient-to-br from-green-50 to-amber-50 rounded-2xl p-8 shadow-lg flex flex-col md:flex-row items-center md:items-start gap-8"
              >
                <img src="/AliTahaCo-FounderFinanceDirector.png" alt="Ali Taha" className="w-40 h-40 rounded-full object-cover shadow-md border-4 border-white" />
                <div className="text-center md:text-left">
                  <h4 className="text-2xl font-bold text-green-800 mb-2">{isRTL ? 'علي طه' : 'Ali Taha'}</h4>
                  <p className="text-green-600 font-semibold mb-4">{t('about.coFounderFinanceDirector')}</p>
                  <p className="text-gray-600 text-sm leading-relaxed">{t('about.aliBio')}</p>
                </div>
              </motion.div>

               {/* Mohamed Hamza */}
               <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="bg-gradient-to-br from-green-50 to-amber-50 rounded-2xl p-8 shadow-lg flex flex-col md:flex-row items-center md:items-start gap-8"
              >
                <img src="/MohamedHamzaCo-FounderOperationsDirector.png" alt="Mohamed Hamza" className="w-40 h-40 rounded-full object-cover shadow-md border-4 border-white" />
                <div className="text-center md:text-left">
                  <h4 className="text-2xl font-bold text-green-800 mb-2">{isRTL ? 'محمد حمزة' : 'Mohamed Hamza'}</h4>
                  <p className="text-green-600 font-semibold mb-4">{t('about.coFounderOperationsDirector')}</p>
                  <p className="text-gray-600 text-sm leading-relaxed">{t('about.mohamedBio')}</p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default About;